import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Statistic, Table, Spin, Typography, Empty } from 'antd';
import {
  UserOutlined,
  UserAddOutlined,
  RiseOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import { Line } from '@ant-design/charts';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import request from '@/utils/request';

const { Title } = Typography;

interface OverviewData {
  totalUsers: number;
  newUsersToday: number;
  dauToday: number;
  totalImages: number;
}

interface GrowthPoint {
  date: string;
  count: number;
}

interface ActivityRecord {
  id: number;
  user: string;
  action: string;
  createdAt: string;
  ip: string | null;
}

const DashboardPage: React.FC = () => {
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [growth, setGrowth] = useState<GrowthPoint[]>([]);
  const [activities, setActivities] = useState<ActivityRecord[]>([]);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingGrowth, setLoadingGrowth] = useState(true);
  const [loadingActivities, setLoadingActivities] = useState(true);

  useEffect(() => {
    fetchOverview();
    fetchGrowth();
    fetchActivities();
  }, []);

  const fetchOverview = async () => {
    try {
      const { data } = await request.get('/admin/analytics/overview');
      setOverview(data);
    } catch {
      setOverview({ totalUsers: 0, newUsersToday: 0, dauToday: 0, totalImages: 0 });
    } finally {
      setLoadingOverview(false);
    }
  };

  const fetchGrowth = async () => {
    try {
      const { data } = await request.get('/admin/analytics/user-growth', {
        params: { days: 30 },
      });
      setGrowth(Array.isArray(data) ? data : []);
    } catch {
      setGrowth([]);
    } finally {
      setLoadingGrowth(false);
    }
  };

  const fetchActivities = async () => {
    try {
      const { data } = await request.get('/admin/analytics/recent-activities', {
        params: { limit: 20 },
      });
      setActivities(Array.isArray(data) ? data : []);
    } catch {
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const activityColumns: ColumnsType<ActivityRecord> = [
    { title: 'User', dataIndex: 'user', key: 'user', width: 150 },
    { title: 'Action', dataIndex: 'action', key: 'action', width: 200 },
    {
      title: 'Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (val: string) => (val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : '-'),
    },
    { title: 'IP', dataIndex: 'ip', key: 'ip', width: 140, render: (v: string | null) => v || '-' },
  ];

  const lineConfig = {
    data: growth,
    xField: 'date',
    yField: 'count',
    smooth: true,
    point: { size: 3, shape: 'circle' as const },
    color: '#1677ff',
    height: 300,
    xAxis: {
      tickCount: 8,
      label: {
        formatter: (text: string) => dayjs(text).format('MM/DD'),
      },
    },
  };

  const statisticCards = [
    { title: 'Total Users', value: overview?.totalUsers ?? 0, icon: <UserOutlined />, color: '#1677ff' },
    { title: 'New Today', value: overview?.newUsersToday ?? 0, icon: <UserAddOutlined />, color: '#52c41a' },
    { title: 'DAU', value: overview?.dauToday ?? 0, icon: <RiseOutlined />, color: '#faad14' },
    { title: 'Total Images', value: overview?.totalImages ?? 0, icon: <PictureOutlined />, color: '#722ed1' },
  ];

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>Dashboard</Title>

      <Spin spinning={loadingOverview}>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {statisticCards.map((item) => (
            <Col xs={24} sm={12} lg={6} key={item.title}>
              <Card hoverable>
                <Statistic
                  title={item.title}
                  value={item.value}
                  prefix={<span style={{ color: item.color, marginRight: 8 }}>{item.icon}</span>}
                />
              </Card>
            </Col>
          ))}
        </Row>
      </Spin>

      <Card title="User Growth (Last 30 Days)" style={{ marginBottom: 24 }}>
        <Spin spinning={loadingGrowth}>
          {growth.length > 0 ? <Line {...lineConfig} /> : <Empty description="No growth data" />}
        </Spin>
      </Card>

      <Card title="Recent Activity">
        <Table<ActivityRecord>
          columns={activityColumns}
          dataSource={activities}
          rowKey="id"
          loading={loadingActivities}
          pagination={false}
          scroll={{ x: 670 }}
          locale={{ emptyText: <Empty description="No recent activities" /> }}
        />
      </Card>
    </div>
  );
};

export default DashboardPage;
